import {
  ChatClient,
  type ChatState,
  type ChatStoreState,
  isChatApiError,
  REQUIRED_ACTION_UPDATE_PASSWORD,
} from '@alternativamais/chat-core';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { armazenamentoLocal } from '../armazenamento/local';

/**
 * Onde a sessão está: conferindo o cookie, sem login, com a senha inicial
 * por trocar (A2), abrindo o gateway (até o READY), pronta, ou sem servidor.
 */
export type Sessao =
  | 'verificando'
  | 'deslogado'
  | 'trocar-senha'
  | 'conectando'
  | 'pronto'
  | 'sem-servidor';

type ContextoChat = {
  cliente: ChatClient;
  sessao: Sessao;
  /** Por que voltou para a tela de entrar (sessão vencida, conta sem chat). */
  aviso: string | null;
  /** Entra com e-mail ou usuário. Senha inicial: vai para `trocar-senha`. */
  entrar(login: string, senha: string): Promise<void>;
  /** A senha que acabou de entrar (para a troca obrigatória não pedir de novo). */
  senhaInicial: string | null;
  /** Depois da troca obrigatória (e do perfil): abre o chat. */
  concluirAcesso(): Promise<void>;
  sair(): Promise<void>;
  tentarDeNovo(): void;
};

const precisaTrocar = (acoes: string[] | undefined) =>
  !!acoes?.includes(REQUIRED_ACTION_UPDATE_PASSWORD);

const Contexto = createContext<ContextoChat | null>(null);

export function criarCliente(aoExpirar: () => void): ChatClient {
  const desktop =
    typeof window !== 'undefined' && 'alternativaDesktop' in window;
  return new ChatClient({
    baseUrl: '/api',
    clientType: 'web',
    appVersion: __APP_VERSION__,
    properties: {
      os:
        typeof navigator !== 'undefined' ? navigator.platform : 'desconhecido',
      browser: desktop ? 'Alternativa Chat Desktop' : 'Alternativa Chat Web',
    },
    storage: armazenamentoLocal,
    onSessionExpired: aoExpirar,
  });
}

/**
 * Um `ChatClient` por aba (o do core). Confere a sessão pelo cookie, abre
 * o gateway e expõe login, saída e o estado da sessão para as telas.
 */
export function ProvedorChat({
  children,
  fabrica = criarCliente,
}: {
  children: ReactNode;
  /** Cria o cliente (os testes passam um com API e gateway falsos). */
  fabrica?: (aoExpirar: () => void) => ChatClient;
}) {
  // A sessão vence fora do React (401 sem refresh, gateway 4004): avisa por evento.
  const [{ cliente, eventos }] = useState(() => {
    const alvo = new EventTarget();
    return {
      eventos: alvo,
      cliente: fabrica(() => alvo.dispatchEvent(new Event('expirou'))),
    };
  });
  const [sessao, setSessao] = useState<Sessao>('verificando');
  const [aviso, setAviso] = useState<string | null>(null);
  const [tentativa, setTentativa] = useState(0);
  const [senhaInicial, setSenhaInicial] = useState<string | null>(null);

  // Em desenvolvimento, o cliente fica no console (`__chat`) para inspecionar.
  useEffect(() => {
    if (import.meta.env.DEV) {
      (window as unknown as { __chat?: ChatClient }).__chat = cliente;
    }
  }, [cliente]);

  // Já teve READY nesta aba (para saber se a sessão "terminou").
  const conectou = useRef(false);
  useEffect(() => {
    const expirou = () => {
      cliente.disconnect();
      // Abrindo sem login, o refresh também falha: aí não é "sessão terminou".
      if (conectou.current) setAviso('Sua sessão terminou. Entre de novo.');
      conectou.current = false;
      setSessao('deslogado');
    };
    eventos.addEventListener('expirou', expirou);
    return () => eventos.removeEventListener('expirou', expirou);
  }, [cliente, eventos]);

  useEffect(
    () =>
      cliente.subscribe((t) => {
        if (t !== 'READY') return;
        conectou.current = true;
        setSessao('pronto');
      }),
    [cliente],
  );

  useEffect(() => {
    let vivo = true;
    cliente.api
      .me()
      .then((eu) => {
        if (!vivo) return;
        // Rascunhos e fila de envio são de cada pessoa (computador do NOC é compartilhado).
        cliente.useAccount(eu.id);
        if (precisaTrocar(eu.required_actions)) {
          setSessao('trocar-senha');
          return;
        }
        setSessao('conectando');
        return cliente.connect();
      })
      .catch((erro: unknown) => {
        if (!vivo) return;
        if (isChatApiError(erro) && erro.isUnavailable) {
          setSessao('sem-servidor');
          return;
        }
        if (isChatApiError(erro) && erro.status === 403) {
          setAviso(erro.message);
        }
        setSessao('deslogado');
      });
    return () => {
      vivo = false;
    };
  }, [cliente, tentativa]);

  // Sem servidor na abertura: tenta de novo sozinho a cada 5 s.
  useEffect(() => {
    if (sessao !== 'sem-servidor') return;
    const espera = window.setTimeout(() => {
      setSessao('verificando');
      setTentativa((valor) => valor + 1);
    }, 5_000);
    return () => window.clearTimeout(espera);
  }, [sessao]);

  // Rede voltou: tenta o gateway na hora (sem esperar a próxima tentativa).
  useEffect(() => {
    const voltou = () => cliente.reconnectNow();
    window.addEventListener('online', voltou);
    return () => window.removeEventListener('online', voltou);
  }, [cliente]);

  const entrar = useCallback(
    async (login: string, senha: string) => {
      const resposta = await cliente.login(login, senha);
      const eu = await cliente.api.me();
      cliente.useAccount(eu.id);
      setAviso(null);
      if (precisaTrocar(resposta.requiredActions ?? eu.required_actions)) {
        setSenhaInicial(senha);
        setSessao('trocar-senha');
        return;
      }
      setSessao('conectando');
      await cliente.connect();
    },
    [cliente],
  );

  const concluirAcesso = useCallback(async () => {
    setSenhaInicial(null);
    // O link pode ter sido aberto com outra conta conectada nesta aba.
    cliente.resetSession();
    conectou.current = false;
    const eu = await cliente.api.me();
    cliente.useAccount(eu.id);
    setSessao('conectando');
    await cliente.connect();
  }, [cliente]);

  const sair = useCallback(async () => {
    await cliente.logout();
    setSenhaInicial(null);
    setAviso(null);
    setSessao('deslogado');
  }, [cliente]);

  const tentarDeNovo = useCallback(() => {
    setSessao('verificando');
    setTentativa((valor) => valor + 1);
  }, []);

  const valor = useMemo(
    () => ({
      cliente,
      sessao,
      aviso,
      entrar,
      senhaInicial,
      concluirAcesso,
      sair,
      tentarDeNovo,
    }),
    [
      cliente,
      sessao,
      aviso,
      entrar,
      senhaInicial,
      concluirAcesso,
      sair,
      tentarDeNovo,
    ],
  );
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSessao(): ContextoChat {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('Fora do ProvedorChat.');
  return contexto;
}

export function useCliente(): ChatClient {
  return useSessao().cliente;
}

/** Uma fatia do estado do chat (referência estável, como vem da store). */
export function useEstado<T>(seletor: (estado: ChatStoreState) => T): T {
  return useStore(useCliente().store, seletor);
}

/** Fatia que é um objeto novo a cada vez (comparado campo a campo). */
export function useEstadoRaso<T>(seletor: (estado: ChatStoreState) => T): T {
  return useStore(useCliente().store, useShallow(seletor));
}

/**
 * Valor derivado (lista ordenada, agrupamento…): recalcula só quando uma
 * das fatias de `dependencias` muda (compare também os IDs usados, pondo
 * eles na lista).
 */
export function useDerivado<T>(
  calcular: (estado: ChatState) => T,
  dependencias: (estado: ChatStoreState) => unknown[],
): T {
  const cliente = useCliente();
  const [lembrar] = useState(() => {
    let anteriores: unknown[] | null = null;
    let valor: T;
    return (
      estado: ChatStoreState,
      calculo: (estado: ChatState) => T,
      fatias: (estado: ChatStoreState) => unknown[],
    ): T => {
      const atuais = fatias(estado);
      const mudou =
        !anteriores ||
        atuais.length !== anteriores.length ||
        atuais.some((fatia, indice) => !Object.is(fatia, anteriores![indice]));
      if (mudou) {
        anteriores = atuais;
        valor = calculo(estado);
      }
      return valor;
    };
  });
  return useStore(cliente.store, (estado) =>
    lembrar(estado, calcular, dependencias),
  );
}
